import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Check, X, CalendarClock } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import TeamLeadFilter from "@/components/TeamLeadFilter";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const TYPES = ["Casual", "Sick", "Earned", "Maternity", "Paternity", "Comp Off", "LOP"];

export default function Leave() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [regs, setRegs] = useState([]);
  const [open, setOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [activeReg, setActiveReg] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [form, setForm] = useState({ type: "Casual", from_date: "", to_date: "", reason: "" });
  const [regForm, setRegForm] = useState({ date: "", reason: "", work_done: "" });
  const [regSettings, setRegSettings] = useState({ monthly_cap: 3 });
  const [refresh, setRefresh] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [tlFilter, setTlFilter] = useState("_all_");

  const canSeeTeam = ["super_admin", "hr", "manager", "team_lead"].includes(user?.role);
  useEffect(() => {
    if (!canSeeTeam) return;
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, [canSeeTeam]);

  const tlByEmp = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.employee_id] = e.team_lead_id; });
    return m;
  }, [employees]);

  const filterByTl = useCallback(
    (rows) => {
      if (!tlFilter || tlFilter === "_all_") return rows;
      return rows.filter((r) => tlByEmp[r.employee_id] === tlFilter);
    },
    [tlFilter, tlByEmp]
  );
  const filteredLeaves = useMemo(() => filterByTl(leaves), [leaves, filterByTl]);
  const filteredRegs = useMemo(() => filterByTl(regs), [regs, filterByTl]);

  const load = useCallback(async () => {
    try {
      const [lvRes, rgRes, stRes] = await Promise.all([
        api.get("/leaves"),
        api.get("/regularizations"),
        api.get("/regularizations/settings").catch(() => ({ data: { monthly_cap: 3 } })),
      ]);
      setLeaves(lvRes.data || []);
      setRegs(rgRes.data || []);
      setRegSettings(stRes.data || { monthly_cap: 3 });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load, refresh]);

  const submitLeave = async () => {
    if (!form.from_date || !form.to_date) return toast.error("Please pick the date range");
    const days = Math.max(1, (new Date(form.to_date) - new Date(form.from_date)) / 86400000 + 1);
    try {
      await api.post("/leaves", {
        ...form, days,
        employee_id: user?.employee_id, employee_name: user?.name,
        status: "pending",
      });
      toast.success("Leave request submitted");
      setOpen(false);
      setForm({ type: "Casual", from_date: "", to_date: "", reason: "" });
      setRefresh((v) => v + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReg = async () => {
    if (!regForm.date) return toast.error("Date is required");
    if (!regForm.reason.trim()) return toast.error("Reason is required");
    try {
      await api.post("/regularizations", regForm);
      toast.success("Regularization request submitted");
      setRegOpen(false);
      setRegForm({ date: "", reason: "", work_done: "" });
      setRefresh((v) => v + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const actLeave = async (id, action) => {
    try {
      await api.post(`/leaves/${id}/${action}`);
      toast.success(`Leave ${action}d`);
      setRefresh((v) => v + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveReg = async (id) => {
    try {
      await api.post(`/regularizations/${id}/approve`);
      toast.success("Regularization approved");
      setRefresh((v) => v + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReject = (r) => { setActiveReg(r); setRejectReason(""); setRejectOpen(true); };
  const confirmReject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/regularizations/${activeReg.id}/reject`, { reason: rejectReason });
      toast.success("Regularization rejected");
      setRejectOpen(false); setActiveReg(null);
      setRefresh((v) => v + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canApprove = ["super_admin", "hr", "manager", "team_lead"].includes(user?.role);
  const balances = [
    { name: "Casual", balance: 8, used: 4 },
    { name: "Sick", balance: 12, used: 2 },
    { name: "Earned", balance: 18, used: 6 },
  ];

  return (
    <div data-testid="leave-page">
      <PageHeader
        title="Leave & Regularization"
        subtitle="Apply for leave, regularize absent days, and action requests from your team."
        action={
          <div className="flex flex-wrap gap-2">
            <Dialog open={regOpen} onOpenChange={setRegOpen}>
              <DialogTrigger asChild>
                <Button data-testid="open-regularize-dialog" variant="outline" className="h-9 border-[#1E88E5] text-[#1E88E5] hover:bg-[#E3F2FD]">
                  <CalendarClock className="h-4 w-4 mr-1.5" /> Regularize absence
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Regularize an absent day</DialogTitle></DialogHeader>
                <p className="text-xs text-slate-500 -mt-2">
                  Use this when you were marked absent but actually worked (e.g. system didn&apos;t capture login, working from home, on-site visit).
                  {regSettings?.monthly_cap > 0 ? <span className="block mt-1 text-amber-600 font-medium">Limit: {regSettings.monthly_cap} request(s) per calendar month</span> : null}
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Date of absence</Label>
                    <Input type="date" value={regForm.date} onChange={(e) => setRegForm({ ...regForm, date: e.target.value })} className="mt-1.5" data-testid="reg-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Reason</Label>
                    <Textarea rows={2} placeholder="Why were you marked absent?" value={regForm.reason} onChange={(e) => setRegForm({ ...regForm, reason: e.target.value })} className="mt-1.5" data-testid="reg-reason-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Work done (optional)</Label>
                    <Textarea rows={2} placeholder="Brief summary of tasks completed that day" value={regForm.work_done} onChange={(e) => setRegForm({ ...regForm, work_done: e.target.value })} className="mt-1.5" data-testid="reg-work-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitReg} data-testid="reg-submit-btn" className="bg-[#1E88E5] hover:bg-[#1565C0]">Submit request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="apply-leave" className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
                  <Plus className="h-4 w-4 mr-1.5" /> Apply leave
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-gray-500">From</Label>
                      <Input type="date" data-testid="leave-from" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-gray-500">To</Label>
                      <Input type="date" data-testid="leave-to" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Reason</Label>
                    <Textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" rows={3} />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="leave-submit" onClick={submitLeave} className="bg-[#F26B3A] hover:bg-[#d85d2f]">Submit</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {balances.map((b) => (
          <Card key={b.name} className="hr-card p-5">
            <div className="text-xs uppercase tracking-wider text-gray-500">{b.name}</div>
            <div className="flex items-baseline gap-2 mt-1.5">
              <div className="hr-kpi">{b.balance}</div>
              <div className="text-xs text-gray-500">/ {b.balance + b.used} used {b.used}</div>
            </div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="leaves">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="leaves" data-testid="tab-leaves">Leave requests ({filteredLeaves.length})</TabsTrigger>
            <TabsTrigger value="regs" data-testid="tab-regs">Regularization requests ({filteredRegs.length})</TabsTrigger>
          </TabsList>
          {canSeeTeam && (
            <TeamLeadFilter value={tlFilter} onChange={setTlFilter} testIdPrefix="leave-tl-filter" />
          )}
        </div>

        <TabsContent value="leaves" className="mt-4">
          <Card className="hr-card overflow-hidden">
            <div className="divide-y divide-gray-100">
              {filteredLeaves.length === 0 && <div className="p-6 text-sm text-gray-500">No leave requests yet.</div>}
              {filteredLeaves.map((l) => (
                <div key={l.id} data-testid={`leave-row-${l.id}`} className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-sm font-medium">{l.employee_name} — {l.type}</div>
                    <div className="text-xs text-gray-500">{l.from_date} → {l.to_date} · {l.days} day(s) · {l.reason}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={l.status} />
                    {canApprove && l.status === "pending" && (
                      <>
                        <Button data-testid={`leave-approve-${l.id}`} size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => actLeave(l.id, "approve")}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button data-testid={`leave-reject-${l.id}`} size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => actLeave(l.id, "reject")}>
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="regs" className="mt-4">
          <Card className="hr-card overflow-hidden">
            <div className="divide-y divide-gray-100">
              {filteredRegs.length === 0 && <div className="p-6 text-sm text-gray-500">No regularization requests yet.</div>}
              {filteredRegs.map((r) => (
                <div key={r.id} data-testid={`reg-row-${r.id}`} className="p-4 flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{r.employee_name} · {r.date}</div>
                    <div className="text-xs text-gray-600 mt-0.5"><b>Reason:</b> {r.reason}</div>
                    {r.work_done ? <div className="text-xs text-gray-500 mt-0.5"><b>Work done:</b> {r.work_done}</div> : null}
                    {r.status === "rejected" && r.rejection_reason ? <div className="text-xs text-rose-600 mt-1"><b>Rejected:</b> {r.rejection_reason}</div> : null}
                    {r.status !== "pending" && r.approver_name ? <div className="text-[11px] text-gray-400 mt-1">By {r.approver_name} ({r.approver_role})</div> : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    {canApprove && r.status === "pending" && (
                      <>
                        <Button data-testid={`reg-approve-${r.id}`} size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => approveReg(r.id)}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button data-testid={`reg-reject-${r.id}`} size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => openReject(r)}>
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject regularization · {activeReg?.employee_name}</DialogTitle></DialogHeader>
          <Textarea rows={3} placeholder="Reason for rejection" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reg-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={confirmReject} className="bg-rose-600 hover:bg-rose-700" data-testid="reg-reject-confirm">Confirm reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
